import { useQuery } from '@tanstack/react-query';

import type { ApiBackendResponse } from '@nf/types/api';
import { PackPopupType, EventPackPopupStates } from '@nf/types/promotion';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cwcRiskFreeBetKeys } from './query-keys';
import { useApiBackendInfo } from '@/hooks/query';

export const fetchPackPopupStates = async (
	popupTypes: Array<PackPopupType>, 
	apiBackendUrl: string, 
	apiBackendToken: string
): Promise<ApiBackendResponse<EventPackPopupStates>> => {
	const response = await fetchComposer.postWithBearer(
		`${apiBackendUrl}/Promotion/GetIsShowStatus`,
		{ body: JSON.stringify({ popupTypes }) }
	)(apiBackendToken);
	
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return await response.json();
};

export const usePackPopupStates = (
	popupTypes: Array<PackPopupType>, 
	{ shouldCheck }: { shouldCheck: boolean }
) => {
	const apiBackendInfo = useApiBackendInfo();
	const shouldEnableRefetch = popupTypes.includes(PackPopupType.IncomingVoucher);

	const { data, isLoading, isError, error, dataUpdatedAt, refetch } = useQuery({
		queryKey: [cwcRiskFreeBetKeys.popupStatus(popupTypes), apiBackendInfo.token],
		queryFn: () => fetchPackPopupStates(popupTypes, apiBackendInfo.url, apiBackendInfo.token),
		staleTime: 60 * 1000,
		cacheTime: 0,
		enabled: shouldCheck && apiBackendInfo.isReady,
		refetchInterval: shouldEnableRefetch ? 60 * 1000 : false,
		refetchIntervalInBackground: false,
	});

	if (isError) {
		console.error(error);
	}

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		dataUpdatedAt,
		refetch,
	};
};